import { useCallback, useMemo, useState } from "react";
import { collectCardIds, ReportCardNode } from "./cardModel";

export interface CardExpansion {
  isOpen: (id: string) => boolean;
  toggle: (id: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  /** At least one card is open (the action row offers Collapse all). */
  anyOpen: boolean;
}

/**
 * Which report cards are open. Cards start closed (title and one value, so
 * a long range reads as a list of days); ids are stable across re-runs, so a
 * card the user opened stays open when the data refreshes.
 */
export function useCardExpansion(cards: ReportCardNode[]): CardExpansion {
  const ids = useMemo(() => collectCardIds(cards), [cards]);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  const toggle = useCallback((id: string) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const expandAll = useCallback(() => setOpen(new Set(ids)), [ids]);
  const collapseAll = useCallback(() => setOpen(new Set()), []);

  return {
    isOpen: (id: string) => open.has(id),
    toggle,
    expandAll,
    collapseAll,
    anyOpen: ids.some((id) => open.has(id)),
  };
}
